import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getALlProductForWeb, getCostToCostProductForWeb, getOtherProductForWeb } from "./shop.service";
import { IShopState, IShopFilter } from "./shopModel";

const initialState: IShopState = {
  loading: false,
  error: "",
  ShopProductList: [],
  totalCount: 0,
  filters: {
    ProductName: "",
    CategoryIDs: "",
    PriceFilter: "",
    Limit: 10,
    Skip: 0,
    IsCostToCost: false,
    IsOnline: false,
  },
  currentPage: 1,
  activeShopPath: "",
};

const shopSlice = createSlice({
  name: "shop",
  initialState,
  reducers: {
    setShopFilter: (state, action: PayloadAction<Partial<IShopFilter>>) => {
      state.filters = { ...state.filters, ...action.payload };
      // Reset skip when filters change
      if (
        action.payload.Limit === undefined &&
        action.payload.Skip === undefined
      ) {
        state.filters.Skip = 0;
        state.currentPage = 1;
      }
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
      state.filters.Skip = (action.payload - 1) * state.filters.Limit;
    },
    clearShopFilters: (state) => {
      state.filters = initialState.filters;
      state.currentPage = 1;
      state.ShopProductList = [];
      state.totalCount = 0;
    },
    clearShopProducts: (state) => {
      state.ShopProductList = [];
      state.totalCount = 0;
    },
    setActiveShopPath: (state, action: PayloadAction<string>) => {
      state.activeShopPath = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getALlProductForWeb.pending, (state) => {
      state.loading = true;
      state.error = "";
      if (state.filters.Skip === 0) {
        state.ShopProductList = [];
      }
    });
    builder.addCase(
      getALlProductForWeb.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.loading = false;
        const newData = action.payload?.Data || [];
        if (state.filters.Skip === 0) {
          state.ShopProductList = newData;
        } else {
          state.ShopProductList = [...state.ShopProductList, ...newData];
        }
        // If the API returns ItemCount/itemcount in the first object or as a separate field
        const firstItem = action.payload?.Data?.[0];
        state.totalCount =
          firstItem?.ItemCount || action.payload?.Data?.length || 0;
      },
    );
    builder.addCase(getALlProductForWeb.rejected, (state, action: any) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.ShopProductList = [];
      state.totalCount = 0;
    });

    // Cost To Cost Cases
    builder.addCase(getCostToCostProductForWeb.pending, (state) => {
      state.loading = true;
      state.error = "";
      if (state.filters.Skip === 0) {
        state.ShopProductList = [];
      }
    });
    builder.addCase(
      getCostToCostProductForWeb.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.loading = false;
        const newData = action.payload?.Data || [];
        if (state.filters.Skip === 0) {
          state.ShopProductList = newData;
        } else {
          state.ShopProductList = [...state.ShopProductList, ...newData];
        }
        const firstItem = action.payload?.Data?.[0];
        state.totalCount =
          firstItem?.ItemCount || action.payload?.Data?.length || 0;
      },
    );
    builder.addCase(getCostToCostProductForWeb.rejected, (state, action: any) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.totalCount = 0;
    });

    // Other Products Cases
    builder.addCase(getOtherProductForWeb.pending, (state) => {
      state.loading = true;
      state.error = "";
      if (state.filters.Skip === 0) {
        state.ShopProductList = [];
      }
    });
    builder.addCase(
      getOtherProductForWeb.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.loading = false;
        const newData = action.payload?.Data || [];
        if (state.filters.Skip === 0) {
          state.ShopProductList = newData;
        } else {
          state.ShopProductList = [...state.ShopProductList, ...newData];
        }
        const firstItem = action.payload?.Data?.[0];
        state.totalCount =
          firstItem?.ItemCount || action.payload?.Data?.length || 0;
      },
    );
    builder.addCase(getOtherProductForWeb.rejected, (state, action: any) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.ShopProductList = [];
      state.totalCount = 0;
    });
  },
});

export const {
  setShopFilter,
  setCurrentPage,
  clearShopFilters,
  clearShopProducts,
  setActiveShopPath,
} = shopSlice.actions;
export default shopSlice.reducer;
